import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    signal,
    ViewEncapsulation,
    inject,
    DestroyRef,
    computed,
    input,
    effect,
    output,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { SiteMapService } from '@fi-app/site-map/site-map.service';
import { IWidgetSettingsModel } from '@fi-shared/widget/widget-settings.model';
import { Subject } from 'rxjs';
import { IWidgetSettingsUpdated, WidgetSharedService } from '@fi-shared/widget/widget-shared.service';
import { FiBackendRequestType, FiRequestsQueueService } from '@fi-shared/fi-shared-services/fi-requests-queue.service';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ISiteMap, ISiteMapContentItem } from '@fi-shared/models/site-map';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IItemAccess, IItemAvailability } from '@fi-shared/models/common';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ISitemapWidgetTagExpandedChange, SitemapWidgetHelperService } from './sitemap-widget-helper.service';

@Component({
    selector: 'fi-widget-content-sitemap',
    templateUrl: './sitemap.component.html',
    styleUrl: './sitemap.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-widget-content-sitemap' },
    standalone: false,
})
export class WidgetContentSitemapComponent implements OnInit, OnChanges {
    @Input() disableEdit: boolean;
    @Input() siteMapSettings: IWidgetSettingsModel;
    @Input() widgetMode: boolean = false;
    afterLoad = output<void>();
    hideEditButton = input<boolean>(false);

    protected siteMap = signal<ISiteMap | null>(null);
    protected updateChildComponent$ = new Subject<void>();
    protected loading = signal(true);
    protected isEmpty = signal(true);
    public isEmpty$ = toObservable(this.isEmpty);

    protected isAllExpanded = signal(false);
    protected expandPanels = signal(false);
    protected expandPanelsRequest = signal(0);
    protected hideLevelOneItemsOnCollapse = signal(false);
    private expandedTagKeys = signal<ReadonlySet<string>>(new Set());
    protected expandToggleButtonText = computed(() => {
        return this.sitemapWidgetHelperService.getExpandToggleButtonText(this.siteMap(), this.isAllExpanded());
    });

    private hasEditAccessToSiteMap = signal<boolean>(undefined);
    protected isHoveringOverContent = signal<boolean>(false);
    protected isHeaderButtonShown = computed(
        () => this.isHoveringOverContent() && this.hasEditAccessToSiteMap() && !this.hideEditButton(),
    );
    private expandAllIfNoTagsEffect = effect(() => {
        const siteMap = this.siteMap();
        if (!siteMap) {
            this.isAllExpanded.set(false);
            this.hideLevelOneItemsOnCollapse.set(false);
            return;
        }
        const isAllExpanded = this.sitemapWidgetHelperService.isInitiallyAllExpanded(siteMap);
        this.isAllExpanded.set(isAllExpanded);
        this.requestExpandPanels(isAllExpanded);
    });

    private readonly siteMapService = inject(SiteMapService);
    private readonly widgetSharedService = inject(WidgetSharedService);
    private readonly fiRequestsQueueService = inject(FiRequestsQueueService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly fiStateService = inject(FiStateService);
    private readonly fiLoading = inject(LoadingService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly sitemapWidgetHelperService = inject(SitemapWidgetHelperService);

    public ngOnInit(): void {
        setTimeout(() => {
            this.loadSiteMapWidgetData();
        }, FiAppSettings.StartDelay);
        this.subscribeOnWidgetSettingsChange();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['siteMapSettings']) {
            this.setHasEditAccessToSiteMap(this.siteMapSettings?.SiteMapId);
        }
    }

    private loadSiteMapWidgetData(): void {
        this.fiRequestsQueueService.addRequestToQueue(
            () => this.getLoadSiteMapWidgetDataPromise(),
            FiBackendRequestType.WidgetSitemap,
        );
    }

    private subscribeOnWidgetSettingsChange(): void {
        this.widgetSharedService
            .getWidgetSettingsChanged$(() => this.siteMapSettings)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: IWidgetSettingsUpdated) => {
                this.cdr.markForCheck();
                this.updateChildComponent$.next();
                if (!data?.skipReloadData) {
                    this.reloadContent();
                }
            });
    }

    private reloadContent(): void {
        this.siteMap.set(undefined);
        this.hasEditAccessToSiteMap.set(undefined);
        this.loadSiteMapWidgetData();
    }

    private setHasEditAccessToSiteMap(siteMapId: number): void {
        if (!siteMapId) {
            this.hasEditAccessToSiteMap.set(undefined);
            return;
        }

        const promise: Promise<void> = this.siteMapService
            .checkHasEditAccessToSiteMap(this.siteMapSettings?.SiteMapId)
            .then((result: IItemAccess) => {
                this.hasEditAccessToSiteMap.set(result.HasEditAccess);
            });

        this.fiLoading.trackPromise(promise);
    }

    private async getLoadSiteMapWidgetDataPromise(): Promise<void> {
        try {
            if (!this.siteMapSettings?.SiteMapId) {
                this.handleNoSiteMapSettings();
                return;
            }

            this.loading.set(true);

            const isAvailable: boolean = await this.checkSiteMapAvailability(this.siteMapSettings.SiteMapId);
            if (isAvailable) {
                await this.loadSiteMapData(this.siteMapSettings.SiteMapId);
            } else {
                this.siteMapSettings.IsEmpty = true;
            }
        } finally {
            this.loading.set(false);
            this.afterLoad.emit();
        }
    }

    private handleNoSiteMapSettings(): void {
        this.siteMap.set(null);
        this.siteMapSettings = this.siteMapSettings || {};
        this.siteMapSettings.IsEmpty = true;
    }

    private async checkSiteMapAvailability(siteMapId: number): Promise<boolean> {
        const result: IItemAvailability = await this.siteMapService.checkSiteMapAvailable(siteMapId);
        return result.IsAvailable;
    }

    private async loadSiteMapData(siteMapId: number): Promise<void> {
        const result: ISiteMap = await this.siteMapService.getSiteMapForWidget(siteMapId);
        // Remove empty tags

        result.TagData.ChildItems = result.TagData.ChildItems.filter((item: ISiteMapContentItem) =>
            this.sitemapWidgetHelperService.isItemNotEmpty(item),
        );
        this.siteMap.set(result);

        const hasItemsWithViewAccess: boolean = result.TagData.ChildItems.some((item: ISiteMapContentItem) =>
            this.sitemapWidgetHelperService.isItemWithViewAccess(item),
        );
        this.siteMapSettings.IsEmpty = !hasItemsWithViewAccess;
        this.isEmpty.set(this.siteMapSettings.IsEmpty);
        this.hideLevelOneItemsOnCollapse.set(false);
        this.expandedTagKeys.set(new Set());
    }

    protected toggleAllExpanded(): void {
        const newIsAllExpanded = !this.isAllExpanded();
        this.isAllExpanded.set(newIsAllExpanded);
        this.expandedTagKeys.set(
            newIsAllExpanded
                ? new Set(this.sitemapWidgetHelperService.getExpandableTagKeys(this.siteMap()))
                : new Set(),
        );
        this.hideLevelOneItemsOnCollapse.set(!newIsAllExpanded);
        this.requestExpandPanels(newIsAllExpanded);
    }

    protected onTagExpandedChange(event: ISitemapWidgetTagExpandedChange): void {
        const expandedTagKeys = new Set(this.expandedTagKeys());
        if (event.expanded) {
            expandedTagKeys.add(event.key);
        } else {
            expandedTagKeys.delete(event.key);
        }
        this.expandedTagKeys.set(expandedTagKeys);

        const expandableTagKeys = this.sitemapWidgetHelperService.getExpandableTagKeys(this.siteMap());
        this.isAllExpanded.set(
            expandableTagKeys.length === 0 || expandableTagKeys.every((key: string) => expandedTagKeys.has(key)),
        );
    }

    private requestExpandPanels(isExpanded: boolean): void {
        this.expandPanels.set(isExpanded);
        this.expandPanelsRequest.update((request) => request + 1);
    }

    protected navigateToSiteMapEdit(): void {
        this.fiStateService.go(FiStateNames.SiteMap.Edit, { Id: this.siteMap().Id });
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
}
